import React, {PureComponent, useEffect, useState} from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import './../chart.scss';
import Select from 'react-select';
import {useTranslation} from '../../../hooks/useTranslation';

const StackedTinyBarChart = ({title, data, options, handleChange}) => {
  const {t} = useTranslation();
  const [newData, setNewData] = useState([]);

  useEffect(() => {
    if (data?.some(item => item?.value?.nfc || item?.value?.total)) {
      console.log('data', data);
      setNewData(data);
    } else {
      setNewData([]);
    }
  }, [data]);

  const renderTooltipContent = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;

    const rowData = payload?.[0]?.payload || {};
    const tooltipValue = rowData?.value || {};
    const nfc = tooltipValue?.nfc ?? 0;
    const partial = tooltipValue?.partial ?? 0;
    const total = tooltipValue?.total ?? 0;
    const value1 = rowData?.value1 ?? 0;
    const value2 = rowData?.value2 ?? 0;
    const value3 = rowData?.value3 ?? partial;
    const seriesColors = {
      value2: '#548CC5',
      value1: '#DF9800',
      value3: '#7B61FF',
      total: '#1F2937',
    };
    const partialLabel = t('components.partial');
    const tooltipRows = [
      {label: t('components.withoutTap'), value: value2, color: seriesColors.value2},
      {label: t('components.withTap'), value: value1, color: seriesColors.value1},
      {label: partialLabel, value: value3, color: seriesColors.value3},
      {label: 'Total', value: total, color: seriesColors.total},
    ];

    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: '10px 12px',
          minWidth: 168,
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.14)',
        }}
      >
        <p
          style={{
            margin: '0 0 8px 0',
            fontWeight: 700,
            fontSize: 14,
            color: '#111827',
          }}
        >
          {label}
        </p>
        {tooltipRows.map(item => (
          <div key={item.label}>
            {item.label === 'Total' ? (
              <div
                style={{
                  borderTop: '1px solid #E5E7EB',
                  margin: '6px 0 8px 0',
                }}
              />
            ) : null}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                marginBottom: 4,
                fontSize: 13,
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    display: 'inline-block',
                  }}
                />
                <span style={{color: '#4B5563'}}>{item.label}</span>
              </div>
              <span style={{color: item.color, fontWeight: 700}}>{item.value}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderRadius = (type, item) => {
    if (type === 'value2') {
      if (item.value1 === 0 && item.value3 === 0) {
        return 4;
      } else {
        return [0, 0, 4, 4];
      }
    }
    if (type === 'value1') {
      if (item.value3 === 0) {
        return [4, 4, 0, 0];
      }

      return 0;
    }
    if (type === 'value3') {
      return [4, 4, 0, 0];
    }
  };

  return (
    <div className='chart mid'>
      <div className='chart__header'>
        <p className='chart__header-title'>{title}</p>
        <Select
          defaultValue={options[0]}
          options={options}
          placeholder={t('components.selectType')}
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
              borderRadius: 8,
              width: 135,
              height: 30,
              fontSize: 14,
              backgroundColor: '#FFFFFF',
              color: '#718096',
            }),
          }}
          onChange={selected => {
            handleChange(selected.value);
          }}
        />
      </div>
      {newData?.length ? (
        <ResponsiveContainer height={287} width='100%'>
          <BarChart
            // width={695}
            height={287}
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid
              strokeDasharray='1'
              vertical={true}
              horizontal={false}
            />
            <XAxis
              dataKey='name'
              stroke='rgba(0, 0, 0, 0.40)'
              tick={{fontSize: 14}}
            />
            <YAxis stroke='rgba(0, 0, 0, 0.40)' />
            <Tooltip content={renderTooltipContent} />
            <Legend />
            <Bar
              dataKey={'value2'}
              name={t('components.withoutTap')}
              fill='#548CC5'
              stackId={'a'}
              barSize={18}
            >
              {newData.map((item, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={'#548CC5'}
                  radius={renderRadius('value2', item)}
                />
              ))}
            </Bar>
            <Bar
              dataKey={'value1'}
              name={t('components.withTap')}
              fill='#DF9800'
              stackId={'a'}
              barSize={18}
            >
              {newData.map((item, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={'#DF9800'}
                  radius={renderRadius('value1', item)}
                />
              ))}
            </Bar>
            <Bar
              dataKey={'value3'}
              name={t('components.partial')}
              fill='#7B61FF'
              stackId={'a'}
              barSize={18}
            >
              {newData.map((item, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={'#7B61FF'}
                  radius={renderRadius('value3', item)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div
          style={{
            width: '100%',
            height: '287px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div>
            <div className='expired__box-img' style={{margin: '0'}}>
              <img src='/assets/no-data.svg' alt='no-data' />
            </div>
            <p className='no-payment-text'>{t('chart.noData')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackedTinyBarChart;
